import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KANAELE } from "@talentkontor/domain";
import { api } from "../../data";
import { LineChart, StackedBars, type Series } from "../../components/charts";
import { money, time } from "../../lib/format";
import { currentLang, t } from "../../lib/sprache";
import { Fortschritt } from "./parts";
import {
  aufteilen, demoRum, heuteZahlen, KANAL_FARBEN, kanalName, kurzDatum, summe, teilVon, trafficGrenzeHinweis, trafficTage, trend, websiteTrichter, type DbAntwort, type RumAntwort,
} from "./traffic-model";

/**
 * Traffic-Dashboard oben in der Betreiber-Konsole (0330, Wunsch des Vertriebs 16.10.2026; Aufbau wie die Startseite von Shopify;
 * 24.10.2026 Konsole als eigene App: oben die Zahlen von heute, die Reichweite als gestapelte Säulen nach Herkunft):
 * Kacheln mit Trend gegenüber der Vorperiode, Besuche je Tag nach Kanal (Linien über die Legende an- und abschaltbar), Akquise-
 * Aktivität und der Weg vom Website-Besuch zum zahlenden Kunden. Besuche kommen über die Serverfunktion betrieb-traffic aus
 * Cloudflare Web Analytics (15 Minuten zwischengespeichert), alles Übrige aus public.platform_traffic. Nur für Plattform-Admins
 * (beide Quellen prüfen das selbst).
 */
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;

export function TrafficDashboard({ tage: gewaehlt, intern }: { tage: number; intern: boolean }) {
  const tage = trafficTage(gewaehlt);
  const qc = useQueryClient();
  const db = useQuery({ queryKey: ["platform_traffic", tage, intern], refetchInterval: 5 * 60_000,
    queryFn: async () => parse<DbAntwort>(await api.read("platform_traffic", { p_days: tage, p_internal: intern })) });
  const heute = db.data?.heute ?? null;
  const rum = useQuery({ queryKey: ["betrieb_traffic", tage, heute], enabled: !!heute, retry: false, staleTime: 5 * 60_000, refetchInterval: 15 * 60_000,
    queryFn: async () => (api.betriebTraffic ? api.betriebTraffic<RumAntwort>(tage) : demoRum(tage, heute!)) });
  const lang = currentLang() === "en" ? "en" : "de";

  if (db.error) return <div className="notice warning" role="alert">{t("Traffic nicht ladbar: ", "Traffic could not be loaded: ")}{db.error instanceof Error ? db.error.message : String(db.error)}</div>;
  if (!db.data) return <p className="muted tk-loading" aria-busy="true">{t("Traffic wird geladen …", "Loading traffic …")}</p>;
  const d = db.data;
  const r = rum.data ?? null;
  const rTage = r ? aufteilen(r.verlauf, r.von).jetzt : [];
  const dTage = aufteilen(d.verlauf, d.von).jetzt;
  const labels = dTage.map((x) => kurzDatum(x.tag, lang));
  const rumAm = new Map(rTage.map((x) => [x.tag, teilVon(x, intern)]));
  // „Sonstige“ (gestrichelt) erscheint in den Säulen schraffiert, so bleibt es von Facebook/LinkedIn (gleiches Grau) unterscheidbar
  const kanalReihen: Series[] = KANAELE.map((k) => ({
    key: k, label: kanalName(k, t), color: KANAL_FARBEN[k].color, dashed: KANAL_FARBEN[k].dashed,
    values: dTage.map((x) => rumAm.get(x.tag)?.kanaele[k] ?? 0),
  }));
  const rumTeile = r ? aufteilen(r.verlauf, r.von) : null;
  const besucheJetzt = rumTeile ? summe(rumTeile.jetzt, (x) => teilVon(x, intern).besuche) : 0;
  const besucheTrend = trend(besucheJetzt, rumTeile ? summe(rumTeile.vorher, (x) => teilVon(x, intern).besuche) : null, t);
  // Inhaber 24.10.2026: jede Linie in eigener Farbe (vorher je zwei gleich, nur gestrichelt unterschieden)
  const akquise: Series[] = [
    { key: "email_gesendet", label: t("E-Mails gesendet", "Emails sent"), color: "var(--tk-s5)", values: dTage.map((x) => x.email_gesendet) },
    { key: "email_geklickt", label: t("E-Mails geklickt", "Emails clicked"), color: "var(--tk-s2)", values: dTage.map((x) => x.email_geklickt) },
    { key: "dm_gesendet", label: t("Instagram-DMs gesendet", "Instagram DMs sent"), color: "var(--tk-s6)", values: dTage.map((x) => x.dm_gesendet) },
    { key: "dm_antwort", label: t("DM-Antworten", "DM replies"), color: "var(--tk-s4)", values: dTage.map((x) => x.dm_antwort) },
  ];
  const zahl = (v: number) => v.toLocaleString(lang === "en" ? "en-GB" : "de-DE");
  const kurz = (v: number) => (v >= 1000 ? `${(v / 1000).toLocaleString(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 1 })}k` : zahl(Math.round(v)));
  const schritte = websiteTrichter({ rum: r, db: d, intern, t });
  const erster = schritte[0]?.n ?? 0;
  const neuLaden = async () => {
    if (!api.betriebTraffic) return;
    qc.setQueryData(["betrieb_traffic", tage, heute], await api.betriebTraffic<RumAntwort>(tage, true));
  };

  return (
    <section className="stack tk-traffic" aria-labelledby="traffic-h">
      <div className="grid tk-heute" aria-label={t("Zahlen von heute", "Today's numbers")}>
        {heuteZahlen({ rum: r, db: d, intern, t, laedt: rum.isPending }).map((k) => (
          <section key={k.key} className="panel stack tk-heute-kachel" aria-label={k.titel}>
            <h2>{k.titel}</h2>
            <div className="metric num">{k.geld ? money(k.wert) : zahl(k.wert)}</div>
            <p>{k.hinweis}</p>
          </section>
        ))}
      </div>

      <div className="row tk-traffic-kopf">
        <h2 id="traffic-h">{t(`Reichweite · letzte ${tage} Tage`, `Reach · last ${tage} days`)}</h2>
        <small className="muted">
          {rum.isPending ? t("Besuche werden geladen …", "Loading visits …")
            : r ? <>{api.mode === "demo" ? t("Demo: erfundene Besucherzahlen", "Demo: made-up visitor numbers") : `${t("Stand", "As of")} ${time(r.stand)} · Cloudflare`}{intern ? "" : t(" · ohne Zugriffe aus PT, US, ES", " · excluding PT, US, ES")}</> : null}
          {api.betriebTraffic && r ? <> · <button type="button" className="quiet tk-traffic-neu" onClick={() => void neuLaden()}>{t("Neu abfragen", "Refresh")}</button></> : null}
        </small>
      </div>
      {gewaehlt > tage && <p className="muted tk-traffic-grenze" id="traffic-grenze">{trafficGrenzeHinweis(gewaehlt, t)}</p>}
      {rum.error && <div className="notice warning" role="status">{t("Besuche gerade nicht verfügbar: ", "Visits unavailable right now: ")}{rum.error instanceof Error ? rum.error.message : String(rum.error)}</div>}

      <section className="panel stack" aria-labelledby="kanal-h">
        <div className="row tk-reichweite-kopf">
          <div>
            <h2 id="kanal-h">{t("Website-Besucher je Tag", "Website visitors per day")}</h2>
            <small className="muted">{t("Echte Besucher nach Herkunft, ohne Bots und eigene Aufrufe.", "Real visitors by source, without bots and own visits.")}</small>
          </div>
          {r && <div className="tk-reichweite-summe">
            <strong className="metric num">{zahl(besucheJetzt)}</strong>
            <small className="muted">{t("gesamt, alle Herkünfte", "total, all sources")}</small>
            <span className={`tk-trend tk-trend-${besucheTrend.richtung}`} aria-label={t(`Veränderung zur Vorperiode ${besucheTrend.text}`, `Change vs. previous period ${besucheTrend.text}`)}>
              {besucheTrend.richtung === "auf" ? "▲ " : besucheTrend.richtung === "ab" ? "▼ " : ""}{besucheTrend.text}</span>
          </div>}
        </div>
        <StackedBars labels={labels} series={kanalReihen} format={zahl} short={kurz} labelColumn={t("Tag", "Day")} height={300}
          caption={t("Website-Besucher je Tag nach Herkunft", "Website visitors per day by source")} emptyText={r ? t("Im Zeitraum keine Besuche.", "No visits in this period.") : undefined} />
        <small className="muted">{t("Herkunft aus der verweisenden Seite; Landungen aus Akquise-Mails (utm_medium=email, /start mit welle-* oder antwort-*) zählt die Website selbst. Herkunft über die Legende ein- und ausblenden.",
          "Source from the referring page; landings from outreach emails (utm_medium=email, /start with welle-* or antwort-*) are counted by the website itself. Toggle sources in the legend.")}</small>
      </section>

      <div className="grid two">
        <section className="panel stack" aria-labelledby="akquise-h">
          <h2 id="akquise-h">{t("Akquise-Aktivität", "Outreach activity")}</h2>
          <LineChart labels={labels} series={akquise} format={zahl} short={kurz} hideInactive compact ganzzahlig labelColumn={t("Tag", "Day")} height={240}
            caption={t("Akquise-Aktivität je Tag", "Outreach activity per day")} emptyText={t("Noch keine Akquise-Zahlen im Zeitraum. DMs lädt der Vertrieb mit tools/ig-dm-hochladen.mjs hoch.", "No outreach numbers in this period yet.")} />
        </section>
        <section className="panel stack" aria-labelledby="web-weg-h">
          <h2 id="web-weg-h">{t("Vom Website-Besuch zum zahlenden Kunden", "From website visit to paying customer")}</h2>
          {erster === 0 ? <p>{t("Im Zeitraum noch keine Besuche oder Konten.", "No visits or accounts in this period yet.")}</p> : schritte.map((s) => (
            <div key={s.schritt} className="stack" style={{ gap: 4 }}>
              <div className="row" style={{ justifyContent: "space-between", gap: 8 }}><span>{s.schritt}</span><strong className="num">{zahl(s.n)} · {Math.round((s.n / erster) * 100)} %</strong></div>
              <Fortschritt pct={(s.n / erster) * 100} label={`${s.schritt}: ${s.n} von ${erster}`} />
            </div>
          ))}
          <small className="muted">{t("Website-Stufen aus Cloudflare (hochgerechnet), ab „Konto angelegt“ die Konten des Zeitraums mit ihrem heutigen Stand.",
            "Website steps from Cloudflare (estimated); from “Account created” the accounts of the period with their current state.")}</small>
        </section>
      </div>
    </section>
  );
}
